import { useMemo, useRef } from 'react';
import type { Permission } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { EmptyState, PageHeader } from '../components/ui.js';
import { HubGraph, HubTools, useHubView, type HubControls } from '../components/HubGraph.js';

// ── the Management section as an interactive 3D map (same presentation as Explore) ───────────────
// A full-bleed "spider web" of the Management pages, clustered by the menu's own sections. Nodes and
// cross-links are filtered to what the signed-in user can actually reach.
const MAP: Array<{ label: string; to: string; perm: Permission; group: string }> = [
  { label: 'Schedule', to: '/schedule', perm: 'schedule:read', group: 'Planning & Delivery' },
  { label: 'Overview', to: '/overview', perm: 'overview:read', group: 'Planning & Delivery' },
  { label: 'Pre-Delivery Alerts', to: '/assembly-alerts', perm: 'overview:read', group: 'Planning & Delivery' },
  { label: 'Engine Orders', to: '/engine-orders', perm: 'overview:read', group: 'Planning & Delivery' },
  { label: 'Washing Plan', to: '/washing', perm: 'overview:read', group: 'Planning & Delivery' },

  { label: 'Assembly Board', to: '/board', perm: 'board:read', group: 'Production' },
  { label: 'Assembly Reporting', to: '/reporting', perm: 'report:read', group: 'Production' },
  { label: 'Deck Flooring', to: '/deck', perm: 'overview:read', group: 'Production' },

  { label: 'Employees', to: '/employees', perm: 'production:read', group: 'People' },
  { label: 'Overtime', to: '/overtime', perm: 'production:read', group: 'People' },

  { label: 'Order Sheet', to: '/order-sheet', perm: 'production:read', group: 'Records' },
  { label: 'Info / Files', to: '/info-files', perm: 'production:read', group: 'Records' },
  { label: 'BOM lists', to: '/bom-lists', perm: 'production:read', group: 'Records' },
];
// semantic cross-links — the strands that tie the clusters into one web.
const CROSS: Array<[string, string]> = [
  ['Schedule', 'Overview'],
  ['Schedule', 'Pre-Delivery Alerts'],
  ['Overview', 'Pre-Delivery Alerts'],
  ['Overview', 'Engine Orders'],
  ['Overview', 'Washing Plan'],
  ['Pre-Delivery Alerts', 'Washing Plan'],
  ['Overview', 'Assembly Board'],
  ['Assembly Board', 'Assembly Reporting'],
  ['Overview', 'Deck Flooring'],
  ['Employees', 'Overtime'],
  ['Order Sheet', 'Overview'],
];

export function ManagementHub() {
  const { can, canSeePath } = useAuth();
  const controls = useRef<HubControls | null>(null);
  const [view, setView] = useHubView();
  const { items, links } = useMemo(() => {
    const nodes = MAP.filter((l) => can(l.perm) && canSeePath(l.to));
    const present = new Set(nodes.map((n) => n.label));
    return {
      items: nodes.map((n) => ({ label: n.label, path: n.to, group: n.group })),
      links: CROSS.filter(([a, b]) => present.has(a) && present.has(b)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, canSeePath]);

  if (items.length === 0) {
    return (
      <>
        <PageHeader title="Management" />
        <EmptyState title="No access" hint="You don't have permission to view any Management pages." />
      </>
    );
  }

  // Full-bleed, box-less map with the same quiet floating glass controls as Explore.
  return (
    <HubGraph fullBleed center="Management" items={items} links={links} controlsRef={controls} view={view}>
      <div className="explore-ui">
        <h1 className="explore-greeting ex-glass">Management</h1>
        <HubTools controls={controls} view={view} onView={setView} />
      </div>
    </HubGraph>
  );
}
